import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Actions as IMPORT_ACTIONS } from '../store/actions/intelligentImportActions';
import { TERMINAL_STATUSES, INTERACTIVE_STATUSES, POLLING_INTERVAL_MS, MAX_POLLING_INTERVAL_MS } from '../constants/importConstants';

const MAX_POLL_ATTEMPTS = 200;

/**
 * Backend can keep `pipeline_status: FAILED` while `function_status.status` is RUNNING after retry-match
 * until the Step Function updates the job. Do not stop polling in that window or the UI never recovers.
 */
const isStepFunctionStillRunning = (job) => {
  if (!job || typeof job !== 'object') return false;
  const fs = job.functionStatus || job.function_status;
  if (!fs) return false;
  const s = String(fs.status || '').toUpperCase();
  return s === 'RUNNING' || s === 'PENDING';
};

/**
 * Polls import job status while the pipeline is running.
 * Uses a self-sustaining setTimeout chain with exponential backoff (3s → 4.5s → … → 15s cap).
 * Stops when the job reaches a terminal or interactive state, or after MAX_POLL_ATTEMPTS.
 * @param {string|null} importId
 */
const useImportPolling = (importId) => {
  const dispatch = useDispatch();
  const polling = useSelector((state) => state.intelligentImport?.polling);
  const timerRef = useRef(null);

  const shouldStop = useCallback((status, job) => {
    if (isStepFunctionStillRunning(job)) return false;
    return TERMINAL_STATUSES.includes(status) || INTERACTIVE_STATUSES.includes(status);
  }, []);

  useEffect(() => {
    if (!importId || !polling) return;

    let cancelled = false;
    let attempt = 0;
    let interval = POLLING_INTERVAL_MS;

    const poll = async () => {
      if (cancelled) return;
      attempt += 1;

      const result = await dispatch(IMPORT_ACTIONS.getImportStatus(importId));
      if (cancelled) return;

      const status = result?.pipelineStatus;

      if (status && shouldStop(status, result)) {
        dispatch(IMPORT_ACTIONS.setPolling(false));
        return;
      }

      if (attempt >= MAX_POLL_ATTEMPTS) {
        dispatch(IMPORT_ACTIONS.setPolling(false));
        return;
      }

      interval = Math.min(interval * 1.5, MAX_POLLING_INTERVAL_MS);
      timerRef.current = setTimeout(poll, interval);
    };

    timerRef.current = setTimeout(poll, interval);

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [importId, polling, dispatch, shouldStop]);

  const startPolling = useCallback(() => {
    dispatch(IMPORT_ACTIONS.setPolling(true));
  }, [dispatch]);

  const stopPolling = useCallback(() => {
    dispatch(IMPORT_ACTIONS.setPolling(false));
    if (timerRef.current) clearTimeout(timerRef.current);
  }, [dispatch]);

  return { polling, startPolling, stopPolling };
};

export default useImportPolling;
